import { Fragment } from 'react'

import { clsx } from 'clsx'

import { useNavigate } from '@tribeplatform/react-components/Link'
import type { StaticField, View } from '@tribeplatform/react-components/Views'
import { Card } from '@tribeplatform/react-ui-kit/Card'

import { groupFieldsByStretchY } from '../../PostsBlock/utils/index.js'
import type { QuickLinksSource } from '../types.js'
import { Field } from './Field.js'

interface Props {
  source: QuickLinksSource
  fields: StaticField[]
  gallerySize: View['gallerySize']
  activeFieldId: string
  width?: number
  className?: string
  preview: boolean
}
export const QuickLinksGalleryItem = ({
  source,
  fields,
  gallerySize,
  activeFieldId,
  width,
  className,
  preview,
}: Props) => {
  const navigate = useNavigate()

  const bannerIsFirst =
    fields.findIndex(
      field =>
        field.id === 'quickLinkGalleryBanner' &&
        field.settings?.bannerStyle === 'banner',
    ) === 0
  const bannerIsMiddle =
    fields.findIndex(
      field =>
        field.id === 'quickLinkGalleryBanner' &&
        field.settings?.bannerStyle === 'banner',
    ) > 0
  const bannerIsLast =
    fields.findIndex(
      field =>
        field.id === 'quickLinkGalleryBanner' &&
        field.settings?.bannerStyle === 'banner',
    ) ===
    fields.length - 1

  return (
    <Card
      onClick={() => {
        if (source.linkUrl) {
          navigate(source.linkUrl)
        }
      }}
      className={clsx(
        source.linkUrl &&
          'cursor-pointer hover:shadow-card-hovered transition-all ease-in-out duration-200',
        'justify-start stretch',
        className,
      )}
      {...(width
        ? { style: { maxWidth: `${width}px`, minWidth: `${width}px` } }
        : {})}
    >
      {bannerIsMiddle && (
        <Card.Content
          className={clsx(
            !bannerIsLast && 'flex-grow-0',
            'flex flex-col gap-4',
          )}
        >
          {groupFieldsByStretchY(
            fields
              .filter((_, index) => {
                const bannerIndex = fields.findIndex(
                  field =>
                    field.id === 'quickLinkGalleryBanner' &&
                    field.settings?.bannerStyle === 'banner',
                )
                return bannerIndex === -1 || index < bannerIndex
              })
              .filter(field => !field.settings?.snapBottom),
          ).map(group => (
            <div
              key={group[0].id}
              className={clsx(
                group[0].settings?.stretchY ? 'flex-1' : 'grow-0 shrink-0',
              )}
            >
              <div className="flex flex-wrap items-center gap-4">
                {group.map(field => (
                  <Field
                    key={field.id}
                    field={field}
                    source={source}
                    activeFieldId={activeFieldId}
                    viewStyle="gallery"
                    gallerySize={gallerySize}
                    preview={preview}
                  />
                ))}
              </div>
            </div>
          ))}
        </Card.Content>
      )}
      {fields.map(field => (
        <Fragment key={field.id}>
          {field.id === 'quickLinkGalleryBanner' &&
            field.settings?.bannerStyle === 'banner' && (
              <Card.Media top={bannerIsFirst} bottom={bannerIsLast}>
                <Field
                  key={field.id}
                  field={field}
                  source={source}
                  activeFieldId={activeFieldId}
                  viewStyle="gallery"
                  gallerySize={gallerySize}
                  preview={preview}
                />
              </Card.Media>
            )}
        </Fragment>
      ))}
      {!bannerIsLast && (
        <Card.Content className="flex flex-col gap-4">
          {groupFieldsByStretchY(
            fields
              .filter((_, index) => {
                const bannerIndex = fields.findIndex(
                  field =>
                    field.id === 'quickLinkGalleryBanner' &&
                    field.settings?.bannerStyle === 'banner',
                )
                return index > bannerIndex
              })
              .filter(field => !field.settings?.snapBottom),
          ).map(group => (
            <div
              key={group[0].id}
              className={clsx(
                group[0].settings?.stretchY ? 'flex-1' : 'grow-0 shrink-0',
              )}
            >
              <div className="flex flex-wrap items-center gap-4">
                {group.map(field => (
                  <Field
                    key={field.id}
                    field={field}
                    source={source}
                    activeFieldId={activeFieldId}
                    viewStyle="gallery"
                    gallerySize={gallerySize}
                    preview={preview}
                  />
                ))}
              </div>
            </div>
          ))}
        </Card.Content>
      )}
      {fields.map(field => (
        <Fragment key={field.id}>
          {field.settings?.snapBottom && (
            <Card.Footer>
              <Field
                key={field.id}
                field={field}
                source={source}
                activeFieldId={activeFieldId}
                viewStyle="gallery"
                gallerySize={gallerySize}
                preview={preview}
              />
            </Card.Footer>
          )}
        </Fragment>
      ))}
    </Card>
  )
}
